<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from "vue";

interface Props {
	gridRef: HTMLElement | null;
	layout: any[];
	isA4: boolean;
	scale: number;
}

const props = withDefaults(defineProps<Props>(), {
	scale: 0.5,
});

const a4HeightPx = (2088 - 120) * props.scale; // 120 is the height of the header.

const pageBreaks = ref<number[]>([]);

const calculatePageBreaks = () => {
	if (!props.gridRef || !props.isA4) return;

	const containerHeight = props.gridRef.getBoundingClientRect().height;
	const breaks = [];
	let currentBreak = a4HeightPx;

	while (currentBreak < containerHeight) {
		breaks.push(currentBreak);
		currentBreak += a4HeightPx;
	}

	pageBreaks.value = breaks;
};

watch(
	() => [props.layout, props.isA4],
	() => nextTick(calculatePageBreaks),
	{ deep: true }
);

onMounted(() => {
	const observer = new ResizeObserver(calculatePageBreaks);
	if (props.gridRef) {
		observer.observe(props.gridRef);
	}
	return () => observer.disconnect();
});
</script>

<template>
	<div
		v-if="isA4"
		class="page-breaks"
		:style="{
			height: gridRef?.getBoundingClientRect().height + 'px',
			position: 'absolute',
			width: '100%',
			left: 0,
			pointerEvents: 'none',
		}"
	>
		<div
			v-for="(breakPoint, index) in pageBreaks"
			:key="index"
			class="page-break"
			:style="{ top: `${breakPoint - 4 * scale}px` }"
			v-tooltip="`Page ${index + 2}`"
		>
			<span class="page-number">P{{ index + 2 }}</span>
		</div>
	</div>
</template>

<style scoped lang="scss">
.page-breaks {
	position: relative;
	flex-shrink: 0;
	margin-top: 0.5rem;
	z-index: 999;
}

.page-break {
	position: absolute;
	width: 100%;
	height: 1px;
	background: linear-gradient(
		to right,
		rgba(0, 0, 0, 0.1) 0%,
		rgba(0, 0, 0, 0.3) 50%,
		rgba(0, 0, 0, 0.1) 100%
	);
	&::before {
		content: "";
		position: absolute;
		top: -4px;
		left: 0;
		right: 0;
		height: 8px;
		background: repeating-linear-gradient(
			45deg,
			rgba(0, 0, 0, 0.05),
			rgba(0, 0, 0, 0.05) 10px,
			transparent 10px,
			transparent 20px
		);
	}
	.page-number {
		position: absolute;
		right: 0;
		top: -12px;
		background: #ffffff;
		border: 1px solid rgba(0, 0, 0, 0.2);
		border-radius: 4px;
		padding: 2px 8px;
		font-size: 12px;
		color: rgba(0, 0, 0, 0.7);
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		pointer-events: auto;
	}
}
</style>
